import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './Layout.css';
import { logout } from '../services/auth';
import api from '../services/api';

const PLANO_LOGOS = {
  trial:   '/logo_trial.png',
  starter: '/logo_starter.png',
  pro:     '/logo_pro.png',
};

const navItems = [
  { label: 'Dashboard',    icon: 'dashboard',         path: '/dashboard' },
  { label: 'Produtos',     icon: 'inventory_2',       path: '/produtos' },
  { label: 'Clientes',     icon: 'groups',            path: '/clientes' },
  { label: 'Laudos',       icon: 'verified',          path: '/laudos' },
  { label: 'Fornecedores', icon: 'factory',           path: '/fornecedores' },
  { label: 'Pedidos',      icon: 'shopping_cart',     path: '/pedidos' },
  { label: 'Orçamentos',   icon: 'payments',          path: '/orcamentos' },
  { label: 'Ordens de Serviço', icon: 'engineering',  path: '/ordens-servico' },
  { label: 'Entradas',     icon: 'arrow_circle_down', path: '/entradas' },
  { label: 'Saídas',       icon: 'arrow_circle_up',   path: '/saidas' },
];

function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notificacoes, setNotificacoes] = useState([]);
  const notifRef = useRef(null);

  useEffect(() => {
    api.get('/notificacoes/')
      .then((res) => setNotificacoes(res.data.itens || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const clean = (v) => (!v || v === 'undefined' || v === 'null') ? '' : v;

  const trialBanner = (() => {
    const plano = localStorage.getItem('plano');
    const expira = localStorage.getItem('trial_expira_em');
    if (plano !== 'trial' || !expira) return null;
    const dias = Math.ceil((new Date(expira + 'T00:00:00') - new Date()) / 86400000);
    if (dias < 0) return { dias: 0, expirado: true };
    return { dias, expirado: false };
  })();

  const firstName   = clean(localStorage.getItem('first_name'));
  const lastName    = clean(localStorage.getItem('last_name'));
  const username    = clean(localStorage.getItem('username'));
  const isStaff     = localStorage.getItem('is_staff') === 'true';
  const displayName = (firstName || lastName)
    ? `${firstName} ${lastName}`.trim()
    : username || 'Usuário';
  const initials = displayName.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();

  const handleLogout = () => { logout(); navigate('/login'); };

  const logoSrc = PLANO_LOGOS[localStorage.getItem('plano')] || '/logo_hope.png';

  const closeSidebar = () => setSidebarOpen(false);

  const handleNotifClick = (link) => {
    setNotifOpen(false);
    navigate(link);
  };

  return (
    <div className="layout-root">

      {/* ── Overlay mobile ───────────────────────────── */}
      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={closeSidebar} />
      )}

      {/* ── Sidebar ──────────────────────────────────── */}
      <aside className={`layout-sidebar${sidebarOpen ? ' sidebar--open' : ''}`}>

        {/* Brand */}
        <div className="sidebar-brand">
          <img src={logoSrc} alt="Hope System" className="sidebar-logo" />
          <button className="sidebar-close-btn" onClick={closeSidebar}>
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {/* Nav */}
        <nav className="sidebar-nav">
          {navItems.map((item) => (
            <a
              key={item.path}
              href={item.path}
              className={`sidebar-nav-item${location.pathname.startsWith(item.path) ? ' active' : ''}`}
              onClick={closeSidebar}
            >
              <span className="material-symbols-outlined">{item.icon}</span>
              <span>{item.label}</span>
            </a>
          ))}
        </nav>

        {/* Footer */}
        <div className="sidebar-footer">
          <button className="sidebar-add-btn" onClick={() => { navigate('/entradas'); closeSidebar(); }}>
            <span className="material-symbols-outlined">add</span>
            <span>Nova Entrada</span>
          </button>
          {localStorage.getItem('is_superuser') === 'true' && (
            <button
              className={`sidebar-nav-item${location.pathname.startsWith('/admin-sistema') ? ' active' : ''}`}
              onClick={() => { navigate('/admin-sistema'); closeSidebar(); }}
            >
              <span className="material-symbols-outlined">admin_panel_settings</span>
              <span>Admin do Sistema</span>
            </button>
          )}
          <button
            className={`sidebar-nav-item${location.pathname.startsWith('/configuracoes') ? ' active' : ''}`}
            onClick={() => { navigate('/configuracoes'); closeSidebar(); }}
          >
            <span className="material-symbols-outlined">settings</span>
            <span>Configurações</span>
          </button>
          <a
            href="https://www.hopesystem.com.br/privacidade"
            target="_blank"
            rel="noopener noreferrer"
            className="sidebar-nav-item"
            style={{ fontSize: '12px', opacity: 0.55, textDecoration: 'none' }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>policy</span>
            <span>Privacidade</span>
          </a>
          <button className="sidebar-nav-item sidebar-logout" onClick={handleLogout}>
            <span className="material-symbols-outlined">logout</span>
            <span>Sair</span>
          </button>
        </div>
      </aside>

      {/* ── Main area ────────────────────────────────── */}
      <div className="layout-main">

        {/* Topbar */}
        <header className="layout-topbar">

          {/* Hamburger — visible only on mobile */}
          <button className="topbar-hamburger" onClick={() => setSidebarOpen(true)}>
            <span className="material-symbols-outlined">menu</span>
          </button>

          <div className="topbar-search">
            <span className="material-symbols-outlined">search</span>
            <input type="text" placeholder="Buscar produtos, pedidos ou clientes..." />
          </div>

          <div className="topbar-right">
            <div className="topbar-actions">
              <div className="topbar-notif-wrap" ref={notifRef}>
                <button className="topbar-icon-btn" onClick={() => setNotifOpen((o) => !o)}>
                  <span className="material-symbols-outlined">notifications</span>
                  {notificacoes.length > 0 && <span className="topbar-notif-dot" />}
                </button>
                {notifOpen && (
                  <div className="topbar-notif-dropdown">
                    <div className="topbar-notif-header">
                      <span>Notificações</span>
                      {notificacoes.length > 0 && (
                        <span className="topbar-notif-count">{notificacoes.length}</span>
                      )}
                    </div>
                    <div className="topbar-notif-list">
                      {notificacoes.length === 0 ? (
                        <p className="topbar-notif-empty">Nenhuma notificação por aqui.</p>
                      ) : (
                        notificacoes.map((n, i) => (
                          <button
                            key={i}
                            className="topbar-notif-item"
                            onClick={() => handleNotifClick(n.link)}
                          >
                            <span className={`material-symbols-outlined topbar-notif-icon topbar-notif-icon--${n.tipo}`}>
                              {n.icone}
                            </span>
                            <div className="topbar-notif-text">
                              <p className="topbar-notif-title">{n.titulo}</p>
                              <p className="topbar-notif-msg">{n.mensagem}</p>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
              <button className="topbar-icon-btn" onClick={() => navigate('/configuracoes')}>
                <span className="material-symbols-outlined">settings</span>
              </button>
            </div>

            <div className="topbar-divider" />

            <div className="topbar-user">
              <div className="topbar-user-info">
                <p className="topbar-user-name">{displayName}</p>
                <p className="topbar-user-role">{isStaff ? 'Administrador' : 'Operador'}</p>
              </div>
              <div className="topbar-avatar">{initials}</div>
            </div>
          </div>
        </header>

        {/* Trial banner */}
        {trialBanner && (
          <div className={`trial-banner${trialBanner.expirado ? ' trial-banner--expired' : trialBanner.dias <= 3 ? ' trial-banner--urgent' : ''}`}>
            <span className="material-symbols-outlined">schedule</span>
            {trialBanner.expirado
              ? 'Seu período de teste expirou. Faça upgrade para continuar.'
              : trialBanner.dias === 0
              ? 'Último dia do seu período de teste!'
              : `${trialBanner.dias} dia${trialBanner.dias !== 1 ? 's' : ''} restante${trialBanner.dias !== 1 ? 's' : ''} no período de teste.`
            }
            <button className="trial-banner-btn" onClick={() => navigate('/configuracoes?tab=plano')}>
              Ver planos
            </button>
          </div>
        )}

        {/* Page content */}
        <main className="layout-content">
          {children}
        </main>
      </div>
    </div>
  );
}

export default Layout;
